// Smart alignment guides (Insta/Canva-style) — snaps a moving/resizing
// element to the page edges/center and to nearby siblings' edges/centers,
// and returns which guide lines to draw.

export const SNAP_THRESHOLD = 6; // px, in unscaled canvas units

function edgesOf(el) {
  return {
    left: el.x,
    right: el.x + el.w,
    centerX: el.x + el.w / 2,
    top: el.y,
    bottom: el.y + el.h,
    centerY: el.y + el.h / 2,
  };
}

function buildTargets(siblings, pageW, pageH, margin) {
  const v = [0, pageW / 2, pageW];
  const h = [0, pageH / 2, pageH];
  if (margin) {
    const left = margin.left;
    const right = pageW - margin.right;
    const top = margin.top;
    const bottom = pageH - margin.bottom;
    v.push(left, right, (left + right) / 2);
    h.push(top, bottom, (top + bottom) / 2);
  }
  siblings.forEach((s) => {
    const e = edgesOf(s);
    v.push(e.left, e.centerX, e.right);
    h.push(e.top, e.centerY, e.bottom);
  });
  return { v, h };
}

function closest(value, targets, threshold) {
  let best = null;
  let bestDelta = threshold + 1;
  targets.forEach((pos) => {
    const d = Math.abs(value - pos);
    if (d < bestDelta) {
      bestDelta = d;
      best = pos;
    }
  });
  return bestDelta <= threshold ? best : null;
}

// Snap while dragging (moving) an element of size w,h to proposed x,y.
export function computeDragSnap({ x, y, w, h }, siblings, pageW, pageH, margin, threshold = SNAP_THRESHOLD) {
  const { v, h: hTargets } = buildTargets(siblings, pageW, pageH, margin);
  const guides = [];

  const vCandidates = [
    { edge: x, offset: 0 },
    { edge: x + w / 2, offset: w / 2 },
    { edge: x + w, offset: w },
  ];
  let snappedX = x;
  let bestVDelta = threshold + 1;
  vCandidates.forEach(({ edge, offset }) => {
    const snap = closest(edge, v, threshold);
    if (snap != null) {
      const delta = Math.abs(edge - snap);
      if (delta < bestVDelta) {
        bestVDelta = delta;
        snappedX = snap - offset;
        guides[0] = { type: "v", pos: snap };
      }
    }
  });

  const hCandidates = [
    { edge: y, offset: 0 },
    { edge: y + h / 2, offset: h / 2 },
    { edge: y + h, offset: h },
  ];
  let snappedY = y;
  let bestHDelta = threshold + 1;
  hCandidates.forEach(({ edge, offset }) => {
    const snap = closest(edge, hTargets, threshold);
    if (snap != null) {
      const delta = Math.abs(edge - snap);
      if (delta < bestHDelta) {
        bestHDelta = delta;
        snappedY = snap - offset;
        guides[1] = { type: "h", pos: snap };
      }
    }
  });

  return { x: Math.round(snappedX), y: Math.round(snappedY), guides: guides.filter(Boolean) };
}

// Snap while resizing (dragging the bottom-right handle) — snaps the
// right/bottom edge of the element to nearby targets.
export function computeResizeSnap({ x, y, w, h }, siblings, pageW, pageH, margin, threshold = SNAP_THRESHOLD) {
  const { v, h: hTargets } = buildTargets(siblings, pageW, pageH, margin);
  const guides = [];

  let snappedW = w;
  const snapRight = closest(x + w, v, threshold);
  if (snapRight != null) {
    snappedW = Math.max(10, snapRight - x);
    guides.push({ type: "v", pos: snapRight });
  }

  let snappedH = h;
  const snapBottom = closest(y + h, hTargets, threshold);
  if (snapBottom != null) {
    snappedH = Math.max(10, snapBottom - y);
    guides.push({ type: "h", pos: snapBottom });
  }

  return { w: Math.round(snappedW), h: Math.round(snappedH), guides };
}